import { useQuery } from '@tanstack/react-query';
import { Banknote, CircleDollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { CardShell, EmptyState } from '../cards/card-shell';
import { formatBrl } from './format';

/**
 * Lista compacta de contas bancarias com saldo individual. Conta marcada
 * como padrao recebe dot indicador. Vazio sugere ir para a tela de config.
 */
export function ContasBancariasWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['financial', 'accounts'],
    queryFn: () => apiClient.bankAccountsList(),
    staleTime: 1000 * 60,
  });

  const accounts = data ?? [];

  return (
    <CardShell title="Contas" icon={Banknote}>
      <div className="flex h-full w-full flex-col gap-2">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            <div className="h-6 w-full animate-pulse rounded bg-[var(--surface-muted)]" />
            <div className="h-6 w-full animate-pulse rounded bg-[var(--surface-muted)]" />
            <div className="h-6 w-full animate-pulse rounded bg-[var(--surface-muted)]" />
          </div>
        ) : isError ? (
          <p className="text-xs text-[var(--fg-muted)]">
            Não foi possível carregar.
          </p>
        ) : accounts.length === 0 ? (
          <EmptyState
            icon={CircleDollarSign}
            message="Nenhuma conta cadastrada."
            cta={{
              label: 'Cadastrar',
              href: '/escritorio/configuracoes/financeiro',
            }}
          />
        ) : (
          <ul className="flex w-full flex-col gap-1.5">
            {accounts.map((acc) => (
              <li
                key={acc.id}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <CircleDollarSign className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" />
                  <span className="truncate text-[var(--fg)]">{acc.name}</span>
                  {acc.isDefault && (
                    <span
                      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--accent)]"
                      title="Conta padrão"
                    />
                  )}
                </span>
                <span
                  className={cn(
                    'shrink-0 font-medium tabular-nums',
                    acc.balance < 0 ? 'text-red-500' : 'text-[var(--fg)]',
                  )}
                >
                  {formatBrl(acc.balance)}
                </span>
              </li>
            ))}
            <li className="mt-2 border-t border-[var(--border)] pt-2 text-right">
              <Link
                to="/escritorio/configuracoes/financeiro"
                className="text-[11px] font-medium text-[var(--accent)] hover:opacity-90"
              >
                Gerenciar
              </Link>
            </li>
          </ul>
        )}
      </div>
    </CardShell>
  );
}
